import React from "react";
import { DA_Checkbox } from "@danishagro/shared/src/components/atoms/Checkbox/Checkbox.component";
import { DA_Input } from "@danishagro/shared/src/components/atoms/Input/Input.component";
import { DA_InputWrapper } from "@danishagro/shared/src/components/atoms/InputWrapper/InputWrapper.component";
import { DA_Select } from "@danishagro/shared/src/components/atoms/Select/Select.component";
import { DA_Text } from "@danishagro/shared/src/components/atoms/Text/Text.component";
import { DA_TextArea } from "@danishagro/shared/src/components/atoms/TextArea/TextArea.component";
import {
    DA_Title,
    DA_TitleTag,
} from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { DA_DatePicker } from "@danishagro/shared/src/components/molecules/DatePicker/DatePicker.component";
import { DA_RadioButton } from "@danishagro/shared/src/components/atoms/RadioButton/RadioButton.component";
import { format } from "date-fns";
import { DA_FileUpload } from "../../../../../../../../components/molecules/FileUpload/FileUpload.component";
import { DynamicFieldProps } from "./DynamicField.interface";
import S from "./DynamicField.module.scss";

export const DynamicField = (props: DynamicFieldProps) => {
    const { field, formData, getError, onInputChange } = props;

    // Error message
    const fieldError = getError(field.alias);

    // Pre values
    const preValues = field.preValues.map((option) => ({
        label: option.caption,
        value: option.value,
        name: option.caption,
    }));

    // Label
    const getLabel = () => {
        if (!field.settings.ShowLabel || field.settings.ShowLabel === "False") return;

        // Indicator
        let indicator = "";
        if (formData.fieldIndicationType === 1 && field.mandatory) indicator = formData.indicator;
        else if (formData.fieldIndicationType === 2 && !field.mandatory)
            indicator = formData.indicator;

        return `${field.caption} ${indicator}`;
    };

    return (
        <>
            {/** Hidden */}
            {field.fieldTypeId === "da206cae-1c52-434e-b21a-4a7c198af877" ? (
                <DA_Input
                    name={field.alias}
                    type="hidden"
                    title={field.caption}
                    defaultValue={field.settings.DefaultValue && field.settings.DefaultValue}
                    className={S.hiddenInput}
                />
            ) : null}

            {/** Input */}
            {field.fieldTypeId === "3f92e01b-29e2-4a30-bf33-9df5580ed52c" ? (
                <DA_Input
                    name={field.alias}
                    title={field.caption}
                    label={getLabel()}
                    placeholder={field.settings.Placeholder}
                    maxLength={
                        field.settings.MaximumLength
                            ? parseInt(field.settings.MaximumLength)
                            : undefined
                    }
                    type={field.settings.FieldType ? field.settings.FieldType : "text"}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    defaultValue={field.settings.DefaultValue && field.settings.DefaultValue}
                    readOnly={field.settings.DefaultValue ? true : undefined}
                    tooltip={field.toolTip}
                    onChange={(value) => onInputChange(field.alias, value)}
                />
            ) : null}

            {/** Dropdown */}
            {field.fieldTypeId === "0dd29d42-a6a5-11de-a2f2-222256d89593" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={getLabel()}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                >
                    <DA_Select
                        id="dropdown"
                        name={field.alias}
                        placeholder={field.settings.SelectPrompt}
                        onChange={(option) => onInputChange(field.alias, option.value)}
                        invalid={fieldError ? true : false}
                        options={preValues}
                        fullWidth
                        defaultValue={
                            field.settings.DefaultValue ? field.settings.DefaultValue : undefined
                        }
                    />
                </DA_InputWrapper>
            ) : null}

            {/** Radiobutton list */}
            {field.fieldTypeId === "903df9b0-a78c-11de-9fc1-db7a56d89593" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={getLabel()}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                    className={fieldError ? S.invalid : null}
                >
                    {field.preValues.map((radio, index) => (
                        <DA_RadioButton
                            key={index}
                            name={field.alias}
                            label={radio.caption}
                            onChange={(value) => onInputChange(field.alias, value)}
                            placeholder={field.settings.Placeholder}
                            value={radio.value}
                            defaultValue={
                                field.settings.DefaultValue
                                    ? field.settings.DefaultValue
                                    : undefined
                            }
                        />
                    ))}
                </DA_InputWrapper>
            ) : null}

            {/** Checkbox list */}
            {field.fieldTypeId === "fab43f20-a6bf-11de-a28f-9b5755d89593" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={getLabel()}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                    className={fieldError ? S.invalid : null}
                >
                    {field.preValues.map((checkbox, index) => (
                        <DA_Checkbox
                            key={index}
                            name={field.alias}
                            placeholder={field.settings.Placeholder}
                            label={checkbox.caption}
                            value={checkbox.value}
                            defaultValue={
                                field.settings.DefaultValue && field.settings.DefaultValue
                            }
                            onChange={(checked) => onInputChange(field.alias, checked)}
                        />
                    ))}
                </DA_InputWrapper>
            ) : null}

            {/** DatePicker */}
            {field.fieldTypeId === "f8b4c3b8-af28-11de-9dd8-ef5956d89593" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={field.caption}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                    className={fieldError ? S.invalid : null}
                >
                    <DA_DatePicker
                        name={field.alias}
                        date={format(new Date(), "yyyy/MM/dd")}
                        datePlaceholder={field.settings.Placeholder}
                        onChange={(date) => onInputChange(field.alias, date)}
                    />
                </DA_InputWrapper>
            ) : null}

            {/** Title and description */}
            {field.fieldTypeId === "e3fbf6c4-f46c-495e-aff8-4b3c227b4a98" ? (
                <>
                    <DA_Title h4 tag={DA_TitleTag.H4} noMargin>
                        {field.settings.Caption}
                    </DA_Title>

                    <DA_Text html={field.settings.BodyText} noPadding />
                </>
            ) : null}

            {/** Long Answer */}
            {field.fieldTypeId === "023f09ac-1445-4bcb-b8fa-ab49f33bd046" ? (
                <DA_TextArea
                    name={field.alias}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    label={getLabel()}
                    placeholder={field.settings.Placeholder}
                    defaultValue={field.settings.DefaultValue && field.settings.DefaultValue}
                    maxRows={
                        field.settings.NumberOfRows ? parseInt(field.settings.NumberOfRows) : 3
                    }
                    maxLength={
                        field.settings.MaximumLength
                            ? parseInt(field.settings.MaximumLength)
                            : undefined
                    }
                    tooltip={field.toolTip}
                    onChange={(value) => onInputChange(field.alias, value)}
                />
            ) : null}

            {/** Checkbox */}
            {field.fieldTypeId === "d5c0c390-ae9a-11de-a69e-666455d89593" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={getLabel()}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                    className={fieldError ? S.invalid : null}
                >
                    <DA_Checkbox
                        name={field.caption}
                        label={field.caption}
                        placeholder={field.settings.Placeholder}
                        value="on"
                        onChange={(checked) => onInputChange(field.alias, checked)}
                        className={fieldError ? S.invalid : null}
                    />
                </DA_InputWrapper>
            ) : null}

            {/** Rich Text */}
            {field.fieldTypeId === "1f8d45f8-76e6-4550-a0f5-9637b8454619" ? (
                <DA_TextArea
                    name={field.alias}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    label={getLabel()}
                    placeholder={field.settings.Placeholder}
                    defaultValue={field.settings.Html}
                    tooltip={field.toolTip}
                    onChange={() => onInputChange(field.alias)}
                />
            ) : null}

            {/** Data consent (checkbox) */}
            {field.fieldTypeId === "a72c9df9-3847-47cf-afb8-b86773fd12cd" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={getLabel()}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                    className={fieldError ? S.invalid : null}
                >
                    <DA_Checkbox
                        name={field.alias}
                        label={<DA_Text html={field.settings.AcceptCopy} noPadding />}
                        placeholder={field.settings.Placeholder}
                        value="on"
                        onChange={(checked) => onInputChange(field.alias, checked)}
                        className={fieldError ? S.invalid : null}
                    />
                </DA_InputWrapper>
            ) : null}

            {/** File Upload */}
            {field.fieldTypeId === "08b8057f-06c9-6ca7-3a42-fd1fc2a46eff" ? (
                <DA_InputWrapper
                    forId={undefined}
                    label={getLabel()}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    tooltip={field.toolTip}
                    className={fieldError ? S.invalid : null}
                >
                    <DA_FileUpload
                        name={field.alias}
                        label={field.settings.Placeholder}
                        onChange={() => onInputChange(field.alias)}
                    />
                </DA_InputWrapper>
            ) : null}

            {/** Unique id input */}
            {field.fieldTypeId === "a1f3e15c-8c96-4239-9f8b-01d7235b73d6" ? (
                <DA_Input
                    name={field.alias}
                    title={field.caption}
                    label={getLabel()}
                    placeholder={field.settings.Placeholder}
                    maxLength={
                        field.settings.MaximumLength
                            ? parseInt(field.settings.MaximumLength)
                            : undefined
                    }
                    type={field.settings.FieldType ? field.settings.FieldType : "number"}
                    invalid={fieldError ? true : false}
                    helperText={fieldError?.message}
                    defaultValue={field.settings.DefaultValue && field.settings.DefaultValue}
                    readOnly={field.settings.DefaultValue ? true : undefined}
                    tooltip={field.toolTip}
                    onChange={(value) => onInputChange(field.alias, value)}
                />
            ) : null}
        </>
    );
};
